'use client'

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { useAgentTicketMutations, useTicketGroups } from '@/hooks/useTickets'
import type { TicketPriority } from '@/types/ticket'
import { CategoryManagementModal } from '@/components/tickets/CategoryManagementModal'
import {
  AttachmentUploader,
  type Attachment,
} from '@/components/tickets/AttachmentUploader'

interface CreateTicketDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateTicketDialog({
  open,
  onOpenChange,
}: CreateTicketDialogProps) {
  const { toast } = useToast()
  const { data: groups } = useTicketGroups()
  const { createTicket } = useAgentTicketMutations()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false)

  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null)
  const [availableCategories, setAvailableCategories] = useState<any[]>([])
  const [attachments, setAttachments] = useState<Attachment[]>([])

  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    title: '',
    description: '',
    priority: 'medium' as TicketPriority,
    categoryId: undefined as number | undefined,
  })

  useEffect(() => {
    if (selectedGroupId && groups) {
      const group = groups.find((g: any) => g.id === selectedGroupId)
      if (group && group.categories) {
        setAvailableCategories(group.categories)
      } else {
        setAvailableCategories([])
      }
      setFormData(prev => ({ ...prev, categoryId: undefined }))
    } else {
      setAvailableCategories([])
    }
  }, [selectedGroupId, groups])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.title.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Subject is required',
        variant: 'destructive',
      })
      return
    }

    if (!formData.customerEmail.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Customer email is required',
        variant: 'destructive',
      })
      return
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.customerEmail)) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a valid email address',
        variant: 'destructive',
      })
      return
    }

    setIsSubmitting(true)
    try {
      const ticketData: any = {
        title: formData.title,
        description: formData.description || undefined,
        priority: formData.priority,
        contactEmail: formData.customerEmail,
        contactName: formData.customerName || undefined,
        categoryId: formData.categoryId,
        attachments: attachments.length > 0 ? attachments : undefined,
      }

      await createTicket.mutateAsync(ticketData)

      toast({
        title: 'Success',
        description: 'Ticket created successfully and assigned to you',
      })

      // Reset form
      setFormData({
        customerName: '',
        customerEmail: '',
        title: '',
        description: '',
        priority: 'medium',
        categoryId: undefined,
      })
      setSelectedGroupId(null)
      setAttachments([])

      onOpenChange(false)
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create ticket',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Ticket</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Customer Information */}
          <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-medium text-sm text-gray-700">
              Customer Information
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="customerName">Customer Name (Optional)</Label>
                <Input
                  id="customerName"
                  value={formData.customerName}
                  onChange={e =>
                    setFormData({ ...formData, customerName: e.target.value })
                  }
                  placeholder="John Doe"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="customerEmail">
                  Customer Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="customerEmail"
                  type="email"
                  required
                  value={formData.customerEmail}
                  onChange={e =>
                    setFormData({ ...formData, customerEmail: e.target.value })
                  }
                  placeholder="customer@example.com"
                />
              </div>
            </div>
          </div>

          {/* Categorization */}
          <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-sm text-gray-700">
                Categorization
              </h3>
              <button
                type="button"
                onClick={() => setIsCategoryModalOpen(true)}
                className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
              >
                Manage Categories
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="group">Ticket Group</Label>
                <Select
                  value={selectedGroupId?.toString() || ''}
                  onValueChange={value =>
                    setSelectedGroupId(value ? Number(value) : null)
                  }
                >
                  <SelectTrigger id="group">
                    <SelectValue placeholder="Select a group (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {groups &&
                      groups.map((group: any) => (
                        <SelectItem key={group.id} value={group.id.toString()}>
                          {group.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedGroupId && availableCategories.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select
                    value={formData.categoryId?.toString() || ''}
                    onValueChange={value =>
                      setFormData({
                        ...formData,
                        categoryId: value ? Number(value) : undefined,
                      })
                    }
                  >
                    <SelectTrigger id="category">
                      <SelectValue placeholder="Select a category (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableCategories.map((category: any) => (
                        <SelectItem
                          key={category.id}
                          value={category.id.toString()}
                        >
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          {/* Ticket Details */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">
                Subject <span className="text-red-500">*</span>
              </Label>
              <Input
                id="title"
                required
                value={formData.title}
                onChange={e =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="Brief description of the issue"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={e =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Detailed description of the issue..."
                minRows={5}
              />
            </div>

            <div className="space-y-2">
              <Label>Attachments (Optional)</Label>
              <AttachmentUploader
                ticketId={0}
                attachments={attachments}
                onAttachmentsChange={setAttachments}
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select
                value={formData.priority}
                onValueChange={value =>
                  setFormData({
                    ...formData,
                    priority: value as TicketPriority,
                  })
                }
              >
                <SelectTrigger id="priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Ticket'}
            </Button>
          </div>

          {/* Info Note */}
          <p className="text-xs text-gray-500 text-center">
            This ticket will be automatically assigned to you
          </p>
        </form>

        {/* Category Management Modal */}
        <CategoryManagementModal
          open={isCategoryModalOpen}
          onOpenChange={setIsCategoryModalOpen}
        />
      </DialogContent>
    </Dialog>
  )
}
